import { useEffect, useMemo, useRef, useState } from "react";
import SubHeader from "../../components/elements/SubHeader";
import LayoutSidebar from "../../components/layout/LayoutSidebar";
import { useLoaderData } from "react-router-dom";
import Sidebar from "../../components/sidebar";
import SidebarCard from "../../components/sidebar/SidebarCard";
import ActionBtnItem from "../../components/utils/ActionBtnItem";
import { formatTemplatePlanDownloadObject } from "../../helper/formatter.helper";
import PresetConfiguration from "../../components/page/presets/PresetConfiguration";
import {
	getAllOrganizationsList,
	getAllResellersList,
	getAllTagsList,
} from "../../helper/data.helper";
import { cn } from "../../lib/utils";
import EmptyTemplate from "../../components/emptystate/EmptyTemplate";
import CrudPresetPopup from "../../components/popups/CrudPresetPopup";
import FilterDropdown from "../../components/utils/FilterDropdown";
import {
	copyTemplatePreset,
	deleteTemplatePreset,
	getTemplatePresets,
} from "../../services/presets.service";
import LoadingBox from "../../components/loading/LoadingBox";
import ConfirmModal from "../../components/popups/ConfirmPopup";
import useNotification from "../../hooks/notification.hook";
import debounce from "lodash.debounce";
import { infiniteScrollBottomHandler } from "../../helper/common.helper";
import EmptyBox from "../../components/emptystate/EmptyBox";
import { useSessionStore } from "../../store/session.store";

const defaultFiltersValue = {
	organizations: [],
	visibility: undefined,
	ownership: undefined,
};

const PlanPresetsPage = () => {
	const { templatePlan, statuses } = useLoaderData();
	const { user } = useSessionStore();

	const [selectedItem, setSelectedItem] = useState(null);
	const [organizations, setOrganizations] = useState([]);
	const [resellers, setResellers] = useState([]);
	const [isLoadingOrganization, setIsLoadingOrganization] = useState(false);
	const [isLoadingResellers, setIsLoadingResellers] = useState(false);
	const [isLoadingTags, setIsLoadingTags] = useState(false);
	const [isLoadingPresets, setIsLoadingPresets] = useState(false);
	const [isLoadingPresetsMore, setIsLoadingPresetsMore] = useState(false);
	const [presets, setPresets] = useState([]);
	const [metaData, setMetaData] = useState({
		current_page: 1,
		last_page: 1,
	});
	const [search, setSearch] = useState("");
	const [tags, setTags] = useState([]);
	const [crudPresetSettings, setCrudPresetSettings] = useState({
		isOpen: false,
		model: null,
	});

	const [duplicatePresetSettings, setDuplicatePresetSettings] = useState({
		isOpen: false,
		model: null,
		loading: false,
	});

	const [deletePresetSettings, setDeletePresetSettings] = useState({
		isOpen: false,
		model: null,
		loading: false,
	});
	const [filters, setFilters] = useState({
		organizations: [],
	});

	const signalRef = useRef(new AbortController());

	const hasFilterActive = useMemo(
		() => JSON.stringify(defaultFiltersValue) !== JSON.stringify(filters),
		[filters]
	);

	const updateFilters = (partialFilter) => {
		setFilters((filters) => ({ ...filters, ...partialFilter }));
	};

	const contentPlan = useMemo(
		() => formatTemplatePlanDownloadObject(templatePlan?.contents),
		[templatePlan?.contents]
	);

	const { helperErrorNotification, openNotification } = useNotification();

	/**
	 * Debounce Search
	 */
	const debouncedSearch = debounce(onSearch, 300);

	/**
	 * Search event function
	 */
	function onSearch(e) {
		const searchModel = e.target.value;

		if (searchModel.trim().length > 0 || search.length) {
			setSearch(searchModel);
		}
	}

	/**
	 * Delete handler
	 */
	const deletePresetHandler = () => {
		if (deletePresetSettings.loading) return;

		setDeletePresetSettings((rest) => ({ ...rest, loading: true }));

		deleteTemplatePreset(
			{ id: deletePresetSettings.model.id },
			() => {
				if (deletePresetSettings?.model?.id === selectedItem?.id) {
					setSelectedItem(null);
				}
				openNotification({
					text: "Configurazione eliminata con successo!",
					status: "success",
				});
				getPresetsList();
				setDeletePresetSettings({ isOpen: false, model: null, loading: false });
			},
			(err) => {
				setDeletePresetSettings((rest) => ({ ...rest, loading: false }));
				helperErrorNotification(err);
			}
		);
	};

	/**
	 * Delete handler
	 */
	const duplicatePresetHandler = async () => {
		if (duplicatePresetSettings.loading) return;

		setDuplicatePresetSettings((rest) => ({ ...rest, loading: true }));

		try {
			const res = await copyTemplatePreset({
				params: { id: duplicatePresetSettings.model.id },
			});
			setSelectedItem(res.data);
			openNotification({
				text: "Configurazione duplicata con successo!",
				status: "success",
			});
			getPresetsList();
		} catch (error) {
			helperErrorNotification(error?.response);
		} finally {
			setDuplicatePresetSettings({
				isOpen: false,
				model: null,
				loading: false,
			});
		}
	};

	/**
	 * Get List organizations
	 */
	const getListOrganizations = async () => {
		if (!templatePlan?.id) return;
		setIsLoadingOrganization(true);
		const filters = {
			template_plan_id: templatePlan.id,
			include: ["organizationType", "reseller", "tags"],
			filter: {
				template_plan_active: true,
			},
		};
		const organizations = await getAllOrganizationsList(filters);
		setIsLoadingOrganization(false);
		setOrganizations(organizations);
	};

	/**
	 * Get List resellers
	 */
	const getListResellers = async () => {
		setIsLoadingResellers(true);
		const resellers = await getAllResellersList();
		setResellers(resellers);
		setIsLoadingResellers(false);
	};

	/**
	 * Get List resellers
	 */
	const getListTags = async () => {
		setIsLoadingTags(true);
		const tags = await getAllTagsList();
		setTags(tags);
		setIsLoadingTags(false);
	};

	/**
	 * Get presets list
	 * @returns {Promise<void>}
	 */
	const getPresetsList = async (page) => {
		signalRef.current.abort();
		signalRef.current = new AbortController();

		if (!page) setIsLoadingPresets(true);
		const body = {
			filter: {
				name: search || undefined,
				accessible_by_organization: filters.organizations.length
					? filters.organizations
					: undefined,
				visibility: filters.visibility || undefined,
				ownership: filters.ownership || undefined,
			},
			page,
			include_policies: 1,
		};

		await getTemplatePresets(
			{ ...body, id: templatePlan.id },
			(res) => {
				if (page > 1) {
					setPresets((presets) => [...presets, ...res.data]);
				} else {
					setPresets(res.data);
				}
				setMetaData({
					current_page: res.meta.current_page,
					last_page: res.meta.last_page,
				});
				setIsLoadingPresets(false);
			},
			() => {},
			{
				signal: signalRef.current.signal,
			}
		);

		setIsLoadingPresetsMore(false);
	};

	/**
	 * Create emit
	 * @param preset
	 */
	const onCreatePresetHandler = (preset) => {
		setSelectedItem(preset);
		getPresetsList();
	};

	/**
	 * Edit emit
	 * @param preset
	 */
	const onEditPresetHandler = (preset) => {
		if (preset.id === selectedItem?.id) {
			setSelectedItem((data) => ({ ...data, name: preset.name }));
		}
		updatePresetHandler(preset);
		//getPresetsList();
	};

	const openCrudPresetHandler = (preset = null) => {
		setCrudPresetSettings({ isOpen: true, model: preset });
	};

	const openDeletePresetHandler = (preset) => {
		setDeletePresetSettings((rest) => ({
			...rest,
			isOpen: true,
			model: preset,
		}));
	};

	const openDuplicatePresetHandler = (preset) => {
		setDuplicatePresetSettings((rest) => ({
			...rest,
			isOpen: true,
			model: preset,
		}));
	};

	const infiniteScrollHandler = (e) => {
		if (metaData.current_page !== metaData.last_page && !isLoadingPresetsMore) {
			infiniteScrollBottomHandler(e, () => {
				setIsLoadingPresetsMore(true);
				getPresetsList(metaData.current_page + 1);
			});
		}
	};

	const updatePresetHandler = (item) => {
		setPresets((prev) => prev.map((p) => (p.id === item.id ? item : p)));
	};

	useEffect(() => {
		getListOrganizations();
	}, [templatePlan?.id]);

	useEffect(() => {
		if (user?.role === "admin") {
			getListResellers();
			getListTags();
		}
	}, [user?.role, templatePlan?.id]);

	useEffect(() => {
		getPresetsList();
	}, [search, filters]);

	return (
		<LayoutSidebar>
			<SubHeader
				title="Gestione configurazioni di stampa"
				titleSmall
				back
				tagElement={{
					name: templatePlan?.name,
					icon: "piano",
				}}
			/>

			<Sidebar hasFilterApplied={hasFilterActive}>
				<Sidebar.Header
					title="Configurazioni"
					actions={
						<button
							type="button"
							onClick={() => openCrudPresetHandler()}
							className="link-interactive flex items-center gap-2"
						>
							<span className="icon_16 plus default icon_mask !bg-interactive-100 -mt-1"></span>
							Nuovo
						</button>
					}
				>
					<div className="flex items-center gap-4">
						<div className="wrap_input_landing icon w-full">
							<input
								onChange={debouncedSearch}
								type="text"
								className="landing"
								placeholder="Ricerca..."
							/>
							<span className="icon_16 default search"></span>
						</div>

						<Sidebar.FilterToggle />
					</div>

					<Sidebar.FilterContent
						title="Filtri avanzati"
						onReset={() => updateFilters(defaultFiltersValue)}
					>
						<FilterDropdown
							options={organizations.map((el) => ({
								label: el.name,
								value: el.id,
							}))}
							activeSelected={filters.organizations}
							onApply={(items) => updateFilters({ organizations: items })}
							toggleLabel="Ente"
							searchFilter
							isLoading={isLoadingOrganization}
						/>

						<FilterDropdown
							options={[
								{
									label: "Tutte le tipologie",
									value: null,
								},
								{
									label: "Solo generici",
									value: "public",
								},
								{
									label: "Solo per enti specifici",
									value: "private",
								},
							]}
							type="radio"
							activeSelected={filters.visibility || null}
							onApply={(item) => updateFilters({ visibility: item || undefined })}
							toggleLabel="Tipologia"
						/>

						<FilterDropdown
							options={[
								{
									label: "Mostra tutte",
									value: null,
								},
								{
									label: "Amministrazione",
									value: "administrator",
								},
								{
									label: "Rivenditore",
									value: "reseller",
								},
							]}
							type="radio"
							activeSelected={filters.ownership || null}
							onApply={(item) => updateFilters({ ownership: item || undefined })}
							toggleLabel="Creato da"
						/>
					</Sidebar.FilterContent>
				</Sidebar.Header>
				<Sidebar.List onScroll={infiniteScrollHandler}>
					{isLoadingPresets ? (
						<LoadingBox blocked="true" />
					) : !presets.length ? (
						<EmptyBox title="Nessun risultato trovato" icon="icon_75 default archive_plan" />
					) : (
						presets.map((preset) => (
							<SidebarCard
								isActive={selectedItem?.id === preset.id}
								onClick={() => setSelectedItem(preset)}
								key={preset.id}
								icon={preset.visibility === "public" ? "piano" : "entity"}
								title={preset.visibility === "public" ? "Generico" : "Enti specifici"}
								description={preset.name}
								actions={
									<>
										<ActionBtnItem
											onClick={() => openDuplicatePresetHandler(preset)}
											icon="duplicate"
											tooltip="Duplica"
										/>
										{preset?.policies?.update && (
											<>
												<ActionBtnItem
													onClick={() => openCrudPresetHandler(preset)}
													icon="edit"
													tooltip="Modifica"
												/>
												<ActionBtnItem
													onClick={() => openDeletePresetHandler(preset)}
													icon="delete"
													tooltip="Elimina"
												/>
											</>
										)}
									</>
								}
							>
								<div className="flex justify-between gap-3">
									<div className="text-dark-70 text-12 line-clamp-1">
										Creato da:{" "}
										{preset.created_by
											? `${preset.created_by?.first_name} ${preset.created_by?.last_name}`
											: "Amministrazione"}
									</div>

									{!preset?.policies?.update && <div className="icon_16 default lock flex-none" />}
								</div>
							</SidebarCard>
						))
					)}
					{isLoadingPresetsMore && <LoadingBox blocked="true" />}
				</Sidebar.List>
			</Sidebar>

			<div className={cn("main_page_sidebar relative", selectedItem?.id && "white_page")}>
				{!selectedItem?.id ? (
					<EmptyTemplate
						className={{ image: "!w-[230px]" }}
						image="/images/empty/empty-list.svg"
						title="Seleziona una configurazione"
						description="Qui vedrai l’anteprima del contenuto della configurazione."
					/>
				) : (
					<PresetConfiguration
						key={selectedItem.id}
						preset={selectedItem}
						data={{
							content: contentPlan,
							statuses: statuses,
							organizations: organizations,
							resellers: resellers,
							tags: tags,
							isLoadingOrganization,
							isLoadingResellers,
							isLoadingTags,
						}}
						onSave={updatePresetHandler}
						subNavRightContent={
							<div className="main_list_counter">
								<ActionBtnItem
									icon="duplicate"
									onClick={() => openDuplicatePresetHandler(selectedItem)}
									tooltip="Duplica"
									big
								/>
								{selectedItem?.policies?.update && (
									<>
										<ActionBtnItem
											icon="edit"
											onClick={() => openCrudPresetHandler(selectedItem)}
											tooltip="Modifica"
											big
										/>

										<ActionBtnItem
											icon="delete"
											onClick={() => openDeletePresetHandler(selectedItem)}
											tooltip="Elimina"
											big
										/>
									</>
								)}
							</div>
						}
					/>
				)}
			</div>

			<CrudPresetPopup
				open={crudPresetSettings.isOpen}
				presetModel={crudPresetSettings.model}
				setOpen={(bool) => setCrudPresetSettings({ isOpen: bool, model: null })}
				templateId={templatePlan?.id}
				onInsert={onCreatePresetHandler}
				onEdit={onEditPresetHandler}
			/>

			<ConfirmModal
				open={deletePresetSettings.isOpen}
				setOpen={(bool) =>
					setDeletePresetSettings((rest) => ({
						...rest,
						isOpen: bool,
						model: null,
					}))
				}
				title="Elimina configurazione"
				subtitle="Configurazione"
				description={deletePresetSettings?.model?.name}
				alertMessage="Attenzione! Questa operazione è irreversibile! <b>Sei sicuro di procedere?</b>"
				ctaLabel="Elimina"
				onConfirm={deletePresetHandler}
				isLoading={deletePresetSettings.loading}
			/>

			<ConfirmModal
				open={duplicatePresetSettings.isOpen}
				setOpen={(bool) =>
					setDuplicatePresetSettings((rest) => ({
						...rest,
						isOpen: bool,
						model: null,
					}))
				}
				title="Duplica configurazione di stampa"
				subtitle="Configurazione"
				description={duplicatePresetSettings?.model?.name}
				ctaLabel="Conferma"
				isSuccess={true}
				onConfirm={duplicatePresetHandler}
				isLoading={duplicatePresetSettings.loading}
			/>
		</LayoutSidebar>
	);
};

export default PlanPresetsPage;
